import React, { useEffect, useState } from 'react';
import { Popover, Typography, Box, Checkbox, Grid2 } from '@mui/material';
import {
	ActiveButtonBadge,
	CheckBoxFormControlLabel,
	PrimaryFormActionButton,
	PrimaryIconButton,
} from '../TableStyles';

import RecordCountBox from '../../../../Components/RecordCountBox'

import { ColumnIconSvg } from '../../Icons/index'

import { useTranslation } from 'react-i18next';
import TableColumnsSearch from './TableColumnsSearch';

const styles = {
	popoverPaper: {
		width: '244px',
		p: 1.5,
		borderRadius: '10px',
		backgroundColor: '#FFFFFF',
		boxShadow: '-2px -2px 4px 0px rgba(151, 151, 151, 0.25), 2px 2px 4px 4px rgba(151, 151, 151, 0.25)',
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between'
	},
	headerText: {
		fontFamily: 'Roboto',
		fontWeight: 700,
		fontSize: '14px',
		lineHeight: '24px',
		color: '#3D4B5A',
		px: 1,
	},
	footer: {
		p: 0,
		display: 'flex',
		justifyContent: 'flex-end',
	},
};


export default function TableColumns({
	initialColumns = [],
	selectedColumns,
	setSelectedColumns,
	maxColumnsAllowed,
	defaultSelectedColumns,
}) {

	const { t } = useTranslation(['common', 'generic_table']);
	const [anchorEl, setAnchorEl] = useState(null);
	const open = Boolean(anchorEl);
	const id = open ? 'filter-popover' : undefined;
	const [newColumns, setNewColumns] = useState([]);
	const [displayOrderColumns, setDisplayOrderColumns] = useState([]);
	const [searchValue, setSearchValue] = useState('');
	const [filteredColumns, setFilteredColumns] = useState(initialColumns);
	const [enableDeselectAllOption, setEnableDeselectAllOption] = useState(true);
	const [hasDefaultsChanged, setHasDefaultsChanged] = useState(false);
	const [hasDefaultSelectionChanged, setHasDefaultSelectionChanged] = useState(false);


	let newColumnLength = newColumns ? newColumns.length : 0;
	const maxColumnsLimit = initialColumns?.length ? Math.min(maxColumnsAllowed, initialColumns.length) : maxColumnsAllowed;

	const areSameLists = (listA, listB) => {
		if (listA.length !== listB.length) return false;
		return listA.every((val, index) => val === listB[index]);
	};

	useEffect(() => {
		const fixedColumns = initialColumns.filter(column => column.fixed !== undefined);
		setEnableDeselectAllOption(fixedColumns.length < newColumns.length);
	}, [newColumns.length, initialColumns]);

	useEffect(() => {
		const isDefaultListSelected = areSameLists(newColumns, defaultSelectedColumns);
		setHasDefaultSelectionChanged(!isDefaultListSelected);
	}, [newColumns, defaultSelectedColumns])

	useEffect(() => {
		setNewColumns(selectedColumns)
		const isDefaultListSelected = areSameLists(defaultSelectedColumns, selectedColumns);
		setHasDefaultsChanged(!isDefaultListSelected);
	}, [selectedColumns, defaultSelectedColumns])

	useEffect(() => {
		setFilteredColumns(initialColumns.filter((column) => column.headerLabel.toLowerCase().includes(searchValue.toLowerCase())));
	}, [searchValue, initialColumns])

	const handleClick = (event, key = null) => {
		setDisplayOrderColumns(selectedColumns);
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setNewColumns(selectedColumns);
		setAnchorEl(null);
	};

	const onDeselectAllColumns = () => {
		const fixedColumns = initialColumns.filter(column => column.fixed !== undefined).map(column => column.dataIndex);
		setNewColumns(fixedColumns);
	}

	const onApplyColumns = () => {
		setSelectedColumns(newColumns)
		setAnchorEl(null)
	}

	const handleCheckboxChange = (columnKey) => (event) => {
		setNewColumns((prevColumns) => {
			if (event.target.checked) {
				return prevColumns.includes(columnKey) ? prevColumns : [...prevColumns, columnKey];
			} else {
				return prevColumns.filter((col) => col !== columnKey);
			}
		});
	}

	const sortColumns = (columns) => {
		const byLabel = (a, b) => (a.headerLabel || '').localeCompare(b.headerLabel || '');
		const fixed = [];
		const selected = [];
		const unselected = [];

		columns.forEach((col) => {
			if (col.fixed !== undefined) {
				fixed.push(col);
			} else if (displayOrderColumns.includes(col.dataIndex)) {
				selected.push(col);
			} else {
				unselected.push(col);
			}
		});

		return [
			...fixed.sort(byLabel),
			...selected.sort(byLabel),
			...unselected.sort(byLabel),
		];
	}

	const handleResetColumns = () => {
		setNewColumns(defaultSelectedColumns);
	}

	const handleSearch = (value) => {
		setSearchValue(value);
	}

	return (
		<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

			<ActiveButtonBadge variant="dot" overlap="circular" invisible={!hasDefaultsChanged}>
				<PrimaryIconButton onClick={handleClick} active={open} data-testid="column-selector-btn">
					<ColumnIconSvg />
				</PrimaryIconButton>
			</ActiveButtonBadge>

			<Popover
				id={id}
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				slotProps={{ paper: { sx: styles.popoverPaper } }}
			>
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					<Box sx={styles.header}>
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
							<Box sx={{ display: 'flex', alignItems: 'center' }}>
								<ColumnIconSvg sx={{ color: '#5E758D' }} />
								<Typography sx={{ mx: 1 }} variant="fw700s14lh24c3D4B5A">{t('generic_table:column_view.title')}</Typography>
								<RecordCountBox count={newColumnLength} total={initialColumns.length} />
							</Box>
							{hasDefaultSelectionChanged && (
								<Box>
									<Typography variant="fw500s12lh24c6D72F6" sx={{cursor: 'pointer'}} onClick={handleResetColumns}>
										{t('actions.reset')}
									</Typography>
								</Box>
							)}
						</Box>
					</Box>

					<Box sx={{ px: 0.5, py: 1 }}>
						{initialColumns.length > 0 && <Typography variant="fw500s10lh24c5E758D" >{t('generic_table:column_view.subtitle', { count: maxColumnsLimit })} </Typography>}
						<Box sx={{mt: 0.5, mb: 1.5}}>
							<TableColumnsSearch value={searchValue} onChange={handleSearch} />
						</Box>
						<Box sx={{ height: '360px', overflow: 'auto' }}>
							{sortColumns(filteredColumns).filter((columnConfig) => columnConfig.headerLabel).map((columnConfig, index) => {
								let checkboxIsDisabled = columnConfig.fixed !== undefined;
								if (newColumnLength >= maxColumnsLimit && !newColumns.includes(columnConfig.dataIndex)) {
									checkboxIsDisabled = true
								}
								return (
									<CheckBoxFormControlLabel
										key={index}
										control={
											<Checkbox
												disableRipple
												disableTouchRipple
												disableFocusRipple
												sx={{ color: '#9F9F9F' }}
												disabled={checkboxIsDisabled}
												checked={newColumns.includes(columnConfig.dataIndex)}
												onChange={handleCheckboxChange(columnConfig.dataIndex)}
											/>
										}
										label={
											<Typography
												variant={checkboxIsDisabled ? 'fw400s12lh24c989898' : 'fw400s12lh24c3D4B5A'}
												sx={{cursor: checkboxIsDisabled ? 'not-allowed': 'pointer' }}
											>
											{columnConfig.headerLabel || "No Label"} </Typography>
										}
									/>
								)
							})}
						</Box>
					</Box>

					<Grid2 container gap={1.5} alignItems={'center'} sx={styles.footer}>
						<Typography
							variant="fw500s12lh24c6D72F6"
							sx={{
								cursor: enableDeselectAllOption ? 'pointer' : 'not-allowed',
								color: enableDeselectAllOption ? '#6D72F6' : '#8F8F8F'
							}}
							onClick={() => enableDeselectAllOption ? onDeselectAllColumns() : null}
						>
							{t('actions.deselectAll')}
						</Typography>
						<PrimaryFormActionButton onClick={onApplyColumns}>
							{t('actions.apply')}
						</PrimaryFormActionButton>
					</Grid2>
				</Box>
			</Popover>
		</Box>
	);
}
